import { UseQueryResult } from 'react-query'
import type { NextRouter } from 'next/router'
import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { setSalesforceDetails } from 'state/booking/actions'
import useAppSelector from 'hooks/useAppSelector'
import { useEffect } from 'react'
import { handleMessageEvent } from './utils'
import { isServer } from 'utils/environment'
import useFetchSalesforceCase from './useSalesforceFetchCase'

export type UseSalesforceCaseReturn = Partial<UseQueryResult> & {
  sfCase: SfCase
  sfAccount: SfAccount
  hasCaseId: boolean
  caseId: string
  results?: SalesForceCaseResults
  isEmbeddedInSf?: boolean
}

export type UseSalesforceCaseOptions = {
  initialSfId?: string
  initialSfObjectType?: string
  ignoreExistingCaseId?: boolean
  initialSfCase?: SfCase
}

const useSalesforceCase = (
  options: UseSalesforceCaseOptions = {
    ignoreExistingCaseId: false,
  }
): UseSalesforceCaseReturn => {
  // `useRouter` from next/router throws "NextRouter was not mounted"
  // when used under the App Router (e.g. when the rebooking modal is
  // mounted from the COE dashboard). Pages Router callers still need
  // the query/dispatch behavior — fall through with no-op router when
  // we're outside that context.
  let router: NextRouter | null = null

  try {
    router = useRouter()
  } catch {
    router = null
  }

  const dispatch = useDispatch()
  const query = router?.query ?? {}
  const { salesforceDetails } = useAppSelector((state) => state.global)

  const sfIdFromQuery = query?.sfId as string
  const sfObjectTypeFromQuery = query?.sfObjectType as string

  options = {
    ...options,
    initialSfId: sfIdFromQuery || options.initialSfId,
    initialSfObjectType: sfObjectTypeFromQuery || options.initialSfObjectType,
  }

  const storedCaseId = options.initialSfId ?? salesforceDetails?.sfId

  const validStoredCaseId =
    storedCaseId && storedCaseId !== 'undefined' ? storedCaseId : ''

  const storedObjectType =
    options.initialSfObjectType ?? salesforceDetails?.sfObjectType

  const hasCaseId =
    Boolean(validStoredCaseId) && storedObjectType?.toLowerCase() === 'case'

  function isEmbeddedInSf(): boolean {
    let isEmbeddedInIframe = false

    if (!isServer()) {
      try {
        isEmbeddedInIframe = window.location !== window.parent.location
      } catch {
        isEmbeddedInIframe = true
      }
    }

    const hasSfDetails = Boolean(
      salesforceDetails?.sfObjectType && salesforceDetails?.sfId
    )
    const hasQueryParams = Boolean(sfIdFromQuery)

    return isEmbeddedInIframe || hasSfDetails || hasQueryParams
  }

  const shouldFetchCaseDetails = hasCaseId || !!options.initialSfCase

  useEffect(() => {
    const effectiveSfId =
      sfIdFromQuery || options.initialSfId || salesforceDetails?.sfId
    const effectiveSfObjectType =
      sfObjectTypeFromQuery ||
      options.initialSfObjectType ||
      salesforceDetails?.sfObjectType

    const shouldUpdateRedux =
      effectiveSfId !== salesforceDetails?.sfId ||
      effectiveSfObjectType !== salesforceDetails?.sfObjectType

    if (shouldUpdateRedux && !isServer()) {
      dispatch(
        setSalesforceDetails({
          sfId: effectiveSfId,
          sfObjectType: effectiveSfObjectType,
        })
      )
    }
  }, [
    sfIdFromQuery,
    sfObjectTypeFromQuery,
    options.initialSfId,
    options.initialSfObjectType,
    salesforceDetails,
  ])

  const caseQueryData = useFetchSalesforceCase(
    validStoredCaseId,
    shouldFetchCaseDetails
  )

  const handleCaseUpdateEvent = (event) =>
    handleMessageEvent(event, caseQueryData, dispatch, 'case')

  useEffect(() => {
    if (isServer()) {
      return
    }

    window.addEventListener('message', handleCaseUpdateEvent)
    return () => window.removeEventListener('message', handleCaseUpdateEvent)
  }, [caseQueryData, dispatch])

  if (!shouldFetchCaseDetails || !caseQueryData?.data) {
    return {
      sfCase: null,
      sfAccount: null,
      hasCaseId: false,
      caseId: '',
      ...{
        isEmbeddedInSf: isEmbeddedInSf(),
        ...caseQueryData,
      },
    }
  }

  return {
    sfCase: caseQueryData.data.sfCase,
    sfAccount: caseQueryData.data.sfAccount,
    results: caseQueryData.data,
    caseId: validStoredCaseId,
    hasCaseId,
    ...{
      isEmbeddedInSf: isEmbeddedInSf(),
    },
    ...caseQueryData,
  }
}

export default useSalesforceCase
